"use client";

/**
 * Ввод телефона с выбором страны и маской — перенесён с сайтов Солнышко, акцент
 * под бренд Автопилота (#0039A6). Самодостаточный: без сторонних библиотек.
 * onChange отдаёт (маскированный национальный номер, dial выбранной страны),
 * родитель собирает полный номер сам: `${dial} ${phone}`.
 */

import { useState, useRef, useEffect } from "react";
import { countries, type Country } from "@/lib/countries";
import { useLocale } from "@/lib/locale-context";

const ACCENT = "#0039A6";
const ACCENT_SOFT = "#EAF0FB";

const POPULAR_CODES = ["RU", "TH", "KZ", "BY", "UA", "UZ", "GE", "AM", "TR", "AE"];
const SORTED_COUNTRIES = [
  ...POPULAR_CODES.map((code) => countries.find((c) => c.code === code)!).filter(Boolean),
  ...countries.filter((c) => !POPULAR_CODES.includes(c.code)),
];

function applyMask(value: string, mask?: string): string {
  if (!mask) return value;
  const digits = value.replace(/\D/g, "").slice(0, 15); // E.164: максимум 15 цифр
  let result = "";
  let di = 0;
  for (let i = 0; i < mask.length && di < digits.length; i++) {
    if (mask[i] === "#") result += digits[di++];
    else result += mask[i];
  }
  // Номер длиннее маски (например DE: 10–11 цифр) — хвост дописываем, не режем.
  if (di < digits.length) result += digits.slice(di);
  return result;
}

function getPlaceholder(mask: string | undefined, fallback: string): string {
  if (!mask) return fallback;
  let i = 0;
  const digits = "9876543210";
  return mask.replace(/#/g, () => digits[i++ % digits.length]);
}

interface Props {
  value: string;
  onChange: (phone: string, dial: string) => void;
  required?: boolean;
  height?: number;
  fontSize?: number;
  // "flat" — убрать боксовое оформление (без рамки/радиуса/фона), чтобы поле
  // вписалось в родителя с инпутами на нижней границе (корпоративная форма).
  flat?: boolean;
}

export default function PhoneInput({ value, onChange, required, height = 48, fontSize = 16, flat = false }: Props) {
  const { t } = useLocale();
  const [country, setCountry] = useState<Country>(SORTED_COUNTRIES[0]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const filtered = search.trim()
    ? SORTED_COUNTRIES.filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.dial.includes(search) ||
        c.code.toLowerCase().includes(search.toLowerCase())
      )
    : SORTED_COUNTRIES;

  return (
    <div style={{ display: "flex", alignItems: "stretch", gap: 6, width: "100%" }}>
      <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
        <button
          type="button"
          onClick={() => { setOpen(!open); setSearch(""); }}
          style={{
            display: "flex",
            alignItems: "center",
            background: flat ? "transparent" : "#fff",
            borderRadius: flat ? 0 : 8,
            border: flat ? "none" : "1px solid #C9D3E6",
            padding: flat ? "0 8px 0 0" : "0 10px",
            height,
            cursor: "pointer",
            gap: 6,
            boxSizing: "border-box",
            fontFamily: "inherit",
          }}
        >
          <span style={{ fontSize: fontSize + 3 }}>{country.flag}</span>
          <span style={{ color: "#000", fontSize, fontWeight: 500 }}>{country.dial}</span>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
            <path d="M3 4.5L6 7.5L9 4.5" stroke="#888" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {open && (
          <div style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            background: "#fff",
            borderRadius: 10,
            boxShadow: "0 4px 20px rgba(20,29,69,0.18)",
            zIndex: 1000,
            width: 280,
            maxHeight: 320,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}>
            <div style={{ padding: "10px 12px 6px" }}>
              <input
                ref={searchRef}
                placeholder={t("phone.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #E0E0E0",
                  borderRadius: 8,
                  fontSize: 14,
                  outline: "none",
                  boxSizing: "border-box",
                }}
                onFocus={(e) => { e.target.style.borderColor = ACCENT; }}
                onBlur={(e) => { e.target.style.borderColor = "#E0E0E0"; }}
              />
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {filtered.length === 0 && (
                <div style={{ padding: "12px 16px", color: "#999", fontSize: 14 }}>{t("phone.notFound")}</div>
              )}
              {filtered.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => {
                    setCountry(c);
                    onChange("", c.dial);
                    setOpen(false);
                    setSearch("");
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    width: "100%",
                    padding: "10px 16px",
                    border: "none",
                    background: country.code === c.code ? ACCENT_SOFT : "transparent",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = ACCENT_SOFT; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = country.code === c.code ? ACCENT_SOFT : "transparent"; }}
                >
                  <span style={{ fontSize: 20 }}>{c.flag}</span>
                  <span style={{ fontSize: 14, color: "#333", flex: 1 }}>{c.name}</span>
                  <span style={{ fontSize: 14, color: "#888" }}>{c.dial}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <input
        type="tel"
        required={required}
        placeholder={getPlaceholder(country.mask, t("phone.number"))}
        value={value}
        onChange={(e) => onChange(formatPhone(e.target.value, country.code), country.dial)}
        style={{
          flex: 1,
          minWidth: 0,
          border: flat ? "none" : "1px solid #C9D3E6",
          borderRadius: flat ? 0 : 8,
          padding: flat ? "0 0 0 8px" : "0 14px",
          height,
          fontSize,
          color: "#000",
          outline: "none",
          boxSizing: "border-box",
          fontFamily: "inherit",
          background: flat ? "transparent" : "#fff",
        }}
        onFocus={(e) => { if (!flat) e.target.style.borderColor = ACCENT; }}
        onBlur={(e) => { if (!flat) e.target.style.borderColor = "#C9D3E6"; }}
      />
    </div>
  );
}
